import { useEffect, useState } from 'react';
import { api } from '../../../api/client';
import styles from './SchoolsPage.module.css';

const ALL_MODULES = [
  { key: 'finance', label: 'Finanzas', icon: '💰' },
  { key: 'chat', label: 'Chat', icon: '💬' },
  { key: 'announcements', label: 'Anuncios', icon: '📢' },
  { key: 'attendance', label: 'Asistencia', icon: '📋' },
  { key: 'grades', label: 'Calificaciones', icon: '📊' },
  { key: 'homework', label: 'Tareas', icon: '📚' },
  { key: 'polls', label: 'Encuestas', icon: '📊' },
  { key: 'calendar', label: 'Calendario', icon: '📅' },
  { key: 'health', label: 'Enfermería', icon: '🏥' },
  { key: 'security', label: 'Seguridad', icon: '🔒' },
  { key: 'behavior', label: 'Conducta', icon: '📋' },
  { key: 'schedules', label: 'Horarios', icon: '📅' },
  { key: 'tickets', label: 'Tickets', icon: '🎫' },
  { key: 'reports', label: 'Reportes', icon: '📊' },
  { key: 'audit', label: 'Auditoría', icon: '📋' },
  { key: 'meals', label: 'Alimentos', icon: '🍽️' },
];

function parseModules(raw: string): Record<string, boolean> {
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

export default function SchoolsPage() {
  const [schools, setSchools] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', slug: '', plan: 'basic' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [modalSchool, setModalSchool] = useState<any>(null);
  const [modalModules, setModalModules] = useState<Record<string, boolean>>({});
  const [modalBrand, setModalBrand] = useState({
    logo_url: '',
    primary_color: 'var(--primary-color)',
  });
  const [userModal, setUserModal] = useState<any>(null);
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    name: '',
    role: 'SCHOOL_ADMIN' as string,
  });
  const [userSaving, setUserSaving] = useState(false);
  const [userError, setUserError] = useState('');

  const fetchSchools = async () => {
    setLoading(true);
    try {
      const data = await api.get<any[]>('/api/super-admin/schools');
      if (Array.isArray(data)) setSchools(data);
    } catch {
      setError('Error al cargar escuelas');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSchools();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/api/super-admin/schools', form);
      setForm({ name: '', slug: '', plan: 'basic' });
      setShowCreate(false);
      fetchSchools();
    } catch (err: any) {
      setError(err.message);
    }
    setSaving(false);
  };

  const openModules = (s: any) => {
    setModalSchool(s);
    setModalModules(parseModules(s.modules));
    try {
      setModalBrand(JSON.parse(s.brandConfig || '{}'));
    } catch {
      setModalBrand({ logo_url: '', primary_color: 'var(--primary-color)' });
    }
  };

  const toggleModule = (key: string) =>
    setModalModules((prev: Record<string, boolean>) => ({ ...prev, [key]: !prev[key] }));

  const saveModules = async () => {
    if (!modalSchool) return;
    setSaving(true);
    try {
      await api.put(`/api/super-admin/schools/${modalSchool.id}`, {
        modules: modalModules,
        brandConfig: modalBrand,
      });
      setModalSchool(null);
      fetchSchools();
    } catch {
      setError('Error al guardar módulos');
    }
    setSaving(false);
  };

  const toggleSchoolActive = async (s: any) => {
    await api.put(`/api/super-admin/schools/${s.id}`, { isActive: !s.isActive });
    fetchSchools();
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userModal) return;
    setUserSaving(true);
    setUserError('');
    try {
      await api.post(`/api/super-admin/schools/${userModal.id}/users`, userForm);
      setUserForm({ email: '', password: '', name: '', role: 'SCHOOL_ADMIN' });
      setUserModal(null);
      fetchSchools();
    } catch (err: any) {
      setUserError(err.message);
    }
    setUserSaving(false);
  };

  const moduleCount = (s: any) => {
    const m = parseModules(s.modules);
    return Object.values(m).filter(Boolean).length;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Gestión de Escuelas</h1>
        <button className={styles.addBtn} onClick={() => setShowCreate(!showCreate)}>
          + Nueva Escuela
        </button>
      </div>
      {error && <div className={styles.error}>{error}</div>}

      {showCreate && (
        <div className={styles.formCard}>
          <h2>Registrar Nueva Institución</h2>
          <form onSubmit={handleCreate} className={styles.form}>
            <input
              placeholder="Nombre de la escuela"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <input
              placeholder="Slug (ej: mi-escuela)"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              required
            />
            <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}>
              <option value="basic">Básico</option>
              <option value="pro">Profesional</option>
            </select>
            <button type="submit" disabled={saving} className={styles.submitBtn}>
              {saving ? 'Creando...' : 'Crear Escuela'}
            </button>
          </form>
        </div>
      )}

      <div className={styles.tableCard}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Institución</th>
              <th>Slug</th>
              <th>Plan</th>
              <th>Usuarios</th>
              <th>Alumnos</th>
              <th>Grupos</th>
              <th>Módulos</th>
              <th>Estatus</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: 20 }}>
                  Cargando...
                </td>
              </tr>
            ) : schools.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: 20 }}>
                  Sin escuelas registradas
                </td>
              </tr>
            ) : (
              schools.map((s: any) => (
                <tr key={s.id}>
                  <td>
                    <strong>{s.name}</strong>
                  </td>
                  <td>{s.slug}</td>
                  <td>
                    <span className={s.plan === 'pro' ? styles.proBadge : styles.basicBadge}>
                      {s.plan === 'pro' ? 'Pro' : 'Básico'}
                    </span>
                  </td>
                  <td>{s._count.users}</td>
                  <td>{s._count.students}</td>
                  <td>{s._count.groups}</td>
                  <td>
                    {moduleCount(s)} / {ALL_MODULES.length}
                  </td>
                  <td>
                    <span className={s.isActive ? styles.activeBadge : styles.inactiveBadge}>
                      {s.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => openModules(s)} className={styles.actionBtn}>
                        ⚙️
                      </button>
                      <button
                        onClick={() => {
                          setUserModal(s);
                          setUserForm({ email: '', password: '', name: '', role: 'SCHOOL_ADMIN' });
                          setUserError('');
                        }}
                        className={styles.actionBtn}
                      >
                        👤
                      </button>
                      <button onClick={() => toggleSchoolActive(s)} className={styles.actionBtn}>
                        {s.isActive ? '⏸' : '▶️'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {userModal && (
        <div className={styles.modalOverlay} onClick={() => setUserModal(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Crear Administrador</h2>
              <button className={styles.modalClose} onClick={() => setUserModal(null)}>
                ✕
              </button>
            </div>
            <p style={{ marginBottom: '1rem', color: '#a0a0b8', fontSize: 14 }}>
              Escuela: <strong>{userModal.name}</strong>
            </p>
            {userError && <div className={styles.error}>{userError}</div>}
            <form onSubmit={handleCreateUser} className={styles.form}>
              <input
                placeholder="Nombre completo"
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                required
              />
              <input
                type="email"
                placeholder="Correo electrónico"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                required
              />
              <input
                type="password"
                placeholder="Contraseña (mín. 8)"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                required
                minLength={8}
              />
              <select
                value={userForm.role}
                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
              >
                <option value="SCHOOL_ADMIN">Administrador</option>
                <option value="STAFF">Personal</option>
                <option value="TEACHER">Docente</option>
              </select>
              <div className={styles.modalFooter}>
                <button
                  type="button"
                  onClick={() => setUserModal(null)}
                  className={styles.cancelBtn}
                >
                  Cancelar
                </button>
                <button type="submit" disabled={userSaving} className={styles.submitBtn}>
                  {userSaving ? 'Creando...' : 'Crear Usuario'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modalSchool && (
        <div className={styles.modalOverlay} onClick={() => setModalSchool(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{modalSchool.name}</h2>
              <button className={styles.modalClose} onClick={() => setModalSchool(null)}>
                ✕
              </button>
            </div>
            <h3 className={styles.sectionTitle}>Módulos Contratados</h3>
            <p className={styles.modalSub}>Activar o desactivar módulos según la contratación</p>
            <div className={styles.moduleGrid}>
              {ALL_MODULES.map((m) => (
                <label key={m.key} className={styles.moduleToggle}>
                  <span>
                    {m.icon} {m.label}
                  </span>
                  <input
                    type="checkbox"
                    checked={!!modalModules[m.key]}
                    onChange={() => toggleModule(m.key)}
                  />
                  <span className={styles.toggleSlider} />
                </label>
              ))}
            </div>
            <h3 className={styles.sectionTitle} style={{ marginTop: '1.5rem' }}>
              Personalización
            </h3>
            <p className={styles.modalSub}>Identidad visual de la institución</p>
            <div className={styles.brandFields}>
              <div className={styles.formField}>
                <label>Logo URL</label>
                <input
                  type="text"
                  placeholder="https://..."
                  value={modalBrand.logo_url}
                  onChange={(e) => setModalBrand({ ...modalBrand, logo_url: e.target.value })}
                />
              </div>
              <div className={styles.formField}>
                <label>Color primario</label>
                <div className={styles.colorPicker}>
                  <input
                    type="color"
                    value={modalBrand.primary_color}
                    onChange={(e) =>
                      setModalBrand({ ...modalBrand, primary_color: e.target.value })
                    }
                  />
                  <input
                    type="text"
                    value={modalBrand.primary_color}
                    onChange={(e) =>
                      setModalBrand({ ...modalBrand, primary_color: e.target.value })
                    }
                    placeholder="#ff2d55"
                  />
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button onClick={() => setModalSchool(null)} className={styles.cancelBtn}>
                Cancelar
              </button>
              <button onClick={saveModules} disabled={saving} className={styles.submitBtn}>
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
